import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { useDebouncedValue } from '@eureca/ui/forms';
import { JobEditCoursesPage as JobEditCoursesPageComponent } from './JobEditCoursesPage.component';
import { JobEditCoursesLoadingFallback } from './JobEditCoursesLoadingFallback';
import { JobEditCoursesErrorFallback } from './JobEditCoursesErrorFallback';
import {
  CoursesByAreaFetchError,
  coursesByAreaQueryOptions,
  courseTypeIdsForGraduation,
} from './courses-by-area-query';
import { JobFetchError, jobByIdQueryOptions } from '@/features/jobs/job-shared/job-by-id-query';
import { isJobDetailReadOnly } from '@/features/jobs/job-shared/job-status';
import { JobMutationError, usePatchJob } from '@/features/jobs/job-shared/job-edit-mutation';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';
import { useJobCompanyLogo } from '@/features/jobs/job-shared/use-job-company-logo';

const INITIAL_PROGRESS = 75;
// EU-162 FR-088f / EU-718 — cap do Core (`requiredCourseGroupIds.max(500)`).
// Depois do dedup curso→grupo a maior vaga de prd tem 260 grupos, então o cap
// deixou de ser alcançável na prática; fica como guarda de payload.
export const MAX_REQUIRED_COURSE_GROUPS = 500;

/**
 * EU-709 RN-16 — mensagem de "escolha ao menos um". Fala em GRUPO de curso
 * porque é essa a unidade que a tela seleciona desde a EU-718.
 */
export const EMPTY_SELECTION_ERROR = 'Selecione ao menos um grupo de curso';

export default function JobEditCoursesPage() {
  const navigate = useNavigate();
  const { jobId } = useParams({
    from: '/vagas/$jobId/editar/formacao/cursos',
  });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const job = jobQuery.data;

  // Course-type filter derivado dos `graduationTypes` da vaga. O BFF
  // aborta com 400 quando o array resultante é vazio. A query usa
  // `enabled` para não dispará-la sem `courseTypeIds`.
  const courseTypeIds = useMemo(
    () => courseTypeIdsForGraduation(job?.graduationTypes ?? []),
    [job?.graduationTypes]
  );

  const patchJob = usePatchJob(jobId);

  const [selectedCourseGroupIds, setSelectedCourseGroupIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAreaIds, setExpandedAreaIds] = useState<string[]>([]);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [capError, setCapError] = useState<string | undefined>();

  // Hydrate one-shot.
  useEffect(() => {
    if (hasHydrated || !job) return;
    setSelectedCourseGroupIds([...(job.requiredCourseGroupIds ?? [])]);
    setHasHydrated(true);
  }, [job, hasHydrated]);

  // 404 — vaga inexistente ou fora de escopo.
  useEffect(() => {
    if (!jobQuery.isError) return;
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
    }
  }, [jobQuery.isError, jobQuery.error, navigate]);

  // Debounce da busca (mantém o input snappy). EU-718 — o valor debounced vai
  // para o BACKEND: entra na queryKey e no querystring. `keepPreviousData` nas
  // queryOptions impede a lista de piscar entre teclas.
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 250);
  const trimmedSearchQuery = debouncedSearchQuery.trim();

  const coursesQuery = useQuery({
    ...coursesByAreaQueryOptions(courseTypeIds, trimmedSearchQuery),
    enabled: courseTypeIds.length > 0,
  });

  // Catálogo COMPLETO (sem busca) — só para o rodapé "N áreas no total" e para
  // o cap. É uma entrada de cache separada, servida do cache enquanto a busca
  // está ativa, então não custa round-trip extra.
  const fullCatalogQuery = useQuery({
    ...coursesByAreaQueryOptions(courseTypeIds),
    enabled: courseTypeIds.length > 0,
  });

  const areas = coursesQuery.data?.areas ?? [];
  const totalAreasCount = fullCatalogQuery.data?.areas.length ?? areas.length;

  const visibleExpandedIds = useMemo(() => {
    if (!trimmedSearchQuery) return expandedAreaIds;
    // Com busca ativa o backend só devolve o que casa — abrir tudo é o
    // comportamento útil (era assim antes, com o filtro em memória).
    const auto = areas.map((area) => String(area.id));
    return Array.from(new Set([...expandedAreaIds, ...auto]));
  }, [trimmedSearchQuery, expandedAreaIds, areas]);

  function handleToggleCourseGroup(courseGroupId: number) {
    setSelectedCourseGroupIds((prev) => {
      if (prev.includes(courseGroupId)) {
        setCapError(undefined);
        return prev.filter((id) => id !== courseGroupId);
      }
      // EU-162 FR-088f — bloquear o 501º grupo (cap do Core).
      if (prev.length >= MAX_REQUIRED_COURSE_GROUPS) {
        setCapError(
          `Você atingiu o limite de ${MAX_REQUIRED_COURSE_GROUPS} grupos de curso por vaga.`
        );
        return prev;
      }
      setCapError(undefined);
      return [...prev, courseGroupId];
    });
  }

  function handleToggleArea(areaId: number) {
    const area = areas.find((entry) => entry.id === areaId);
    if (!area) return;
    const groupIds = area.courseGroups.map((group) => group.id);
    setSelectedCourseGroupIds((prev) => {
      const currentSet = new Set(prev);
      const allSelected = groupIds.every((id) => currentSet.has(id));
      if (allSelected) {
        for (const id of groupIds) currentSet.delete(id);
        setCapError(undefined);
        return Array.from(currentSet);
      }
      // Adicionar todos os grupos da área respeitando o cap. Se passar o
      // limite, adiciona quantos couberem e mostra mensagem.
      const next = Array.from(currentSet);
      let capped = false;
      for (const id of groupIds) {
        if (currentSet.has(id)) continue;
        if (next.length >= MAX_REQUIRED_COURSE_GROUPS) {
          capped = true;
          break;
        }
        next.push(id);
      }
      setCapError(
        capped
          ? `Você atingiu o limite de ${MAX_REQUIRED_COURSE_GROUPS} grupos de curso por vaga.`
          : undefined
      );
      return next;
    });
  }

  async function handleSubmit() {
    // EU-569 (BLQ-003) — modo visualização: "Avançar" só navega de volta ao
    // hub, sem persistir.
    if (isJobDetailReadOnly(job)) {
      void navigate({ to: '/vagas/$jobId', params: { jobId } });
      return;
    }
    // EU-709 RN-16 — a segunda metade da regra condicional da Formação:
    // escolhido especificar o curso, a escolha torna-se obrigatória. Esta tela
    // SÓ é alcançada por quem escolheu `quero-especificar`, então sair daqui
    // sem nada selecionado deixaria a seção num estado que a própria escolha do
    // consultor contradiz.
    //
    // A unidade de seleção é o GRUPO DE CURSO desde a EU-718 — a regra é a
    // mesma, o que mudou foi o que o consultor escolhe.
    if (selectedCourseGroupIds.length === 0) {
      setCapError(EMPTY_SELECTION_ERROR);
      return;
    }
    try {
      await patchJob.mutateAsync({ requiredCourseGroupIds: selectedCourseGroupIds });
      toast('Alterações salvas com sucesso', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      void navigate({
        to: '/vagas/$jobId',
        params: { jobId },
      });
    } catch (err) {
      const status = err instanceof JobMutationError ? err.status : 0;
      if (status === 404) {
        toast.error('Vaga não encontrada.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para editar essa vaga.');
      } else {
        toast.error('Não foi possível salvar as alterações. Tente novamente.');
      }
    }
  }

  function handleBack() {
    // Voltar descarta as mudanças silenciosamente (decisão de produto:
    // save explícito via botão "Salvar alterações").
    void navigate({
      to: '/vagas/$jobId',
      params: { jobId },
    });
  }

  // Surface a load failure on the catalogue the same way other pages do.
  useEffect(() => {
    if (!coursesQuery.isError) return;
    const status =
      coursesQuery.error instanceof CoursesByAreaFetchError ? coursesQuery.error.status : 0;
    if (status >= 500 || status === 0) {
      toast.error('Não foi possível carregar a lista de grupos de curso.');
    }
  }, [coursesQuery.isError, coursesQuery.error]);

  const { companyLogoUrl, companyName } = useJobCompanyLogo(job?.programId);

  // Loading state — primeiro carregamento da vaga.
  if (jobQuery.isLoading) {
    return <JobEditCoursesLoadingFallback />;
  }

  // Erro de carregamento da vaga (≠ 404).
  if (jobQuery.isError) {
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status !== 404) {
      return <JobEditCoursesErrorFallback onRetry={() => void jobQuery.refetch()} />;
    }
  }

  return (
    <JobEditCoursesPageComponent
      jobName={job?.name ?? ''}
      companyLogoUrl={companyLogoUrl}
      companyName={companyName}
      areas={areas}
      totalAreasCount={totalAreasCount}
      searchQuery={debouncedSearchQuery}
      searchInputValue={searchQuery}
      selectedCourseGroupIds={selectedCourseGroupIds}
      expandedAreaIds={visibleExpandedIds}
      progress={INITIAL_PROGRESS}
      isSubmitting={patchJob.isPending}
      capError={capError}
      maxRequiredCourseGroups={MAX_REQUIRED_COURSE_GROUPS}
      hasGraduationTypes={courseTypeIds.length > 0}
      readOnly={isJobDetailReadOnly(job)}
      onSearchChange={setSearchQuery}
      onExpandedChange={setExpandedAreaIds}
      onToggleCourseGroup={handleToggleCourseGroup}
      onToggleArea={handleToggleArea}
      onBack={handleBack}
      onSubmit={() => {
        void handleSubmit();
      }}
    />
  );
}
